import {
  useCallback,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

import { FAB_MAX_SIZE_PX, FAB_MIN_SIZE_PX, FAB_SIZE_PX } from "../constants";
import { localGet, localSet, safeJsonParse } from "../utils/safe-storage";

interface UseResizableFabArgs {
  storageKey: string;
}

interface UseResizableFabResult {
  size: number;
  resizing: boolean;
  onResizePointerDown: (e: ReactPointerEvent) => void;
  /** Mirror of the FAB drag suppression — swallow the click that ends a resize. */
  consumeResizeClick: () => boolean;
}

const clampSize = (s: number) =>
  Math.min(Math.max(s, FAB_MIN_SIZE_PX), FAB_MAX_SIZE_PX);

const initSize = (storageKey: string): number => {
  const stored = safeJsonParse<number | null>(localGet(storageKey), null);
  return typeof stored === "number" ? clampSize(stored) : FAB_SIZE_PX;
};

export const useResizableFab = ({
  storageKey,
}: UseResizableFabArgs): UseResizableFabResult => {
  const [size, setSize] = useState<number>(() => initSize(storageKey));
  const [resizing, setResizing] = useState(false);
  const sizeRef = useRef(size);
  sizeRef.current = size;
  const suppressClickRef = useRef(false);

  const persist = useCallback(
    (s: number) => localSet(storageKey, JSON.stringify(s)),
    [storageKey],
  );

  const onResizePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startY = e.clientY;
      const origin = sizeRef.current;
      suppressClickRef.current = true;
      setResizing(true);

      const prevUserSelect = document.body.style.userSelect;
      document.body.style.userSelect = "none";

      const move = (ev: PointerEvent) => {
        ev.preventDefault();
        // Bottom-left handle: down-left grows, up-right shrinks.
        const delta = (startX - ev.clientX + (ev.clientY - startY)) / 2;
        setSize(clampSize(origin + delta));
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        document.body.style.userSelect = prevUserSelect;
        setResizing(false);
        persist(sizeRef.current);
      };

      window.addEventListener("pointermove", move, { passive: false });
      window.addEventListener("pointerup", up);
    },
    [persist],
  );

  const consumeResizeClick = useCallback(() => {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    return true;
  }, []);

  return { size, resizing, onResizePointerDown, consumeResizeClick };
};
